import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import keycloakAuth from '../services/KeycloakAuth';

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  /** Redirects to the Keycloak-hosted login page. */
  login: () => void;
  logout: () => void;
  hasRole: (role: string) => boolean;
  getRoles: () => string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize the Keycloak adapter on mount. This also processes the
    // redirect back from the Keycloak login page (code exchange) and silently
    // restores an existing SSO session on refresh.
    const initAuth = async () => {
      const authenticated = await keycloakAuth.init();
      setIsAuthenticated(authenticated);
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = () => {
    keycloakAuth.login();
  };

  const logout = () => {
    keycloakAuth.logout();
    setIsAuthenticated(false);
  };

  const hasRole = (role: string): boolean => {
    return keycloakAuth.hasRole(role);
  };

  const getRoles = (): string[] => {
    return keycloakAuth.getRoles();
  };

  const value: AuthContextType = {
    isAuthenticated,
    loading,
    login,
    logout,
    hasRole,
    getRoles,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
